import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { toast } from "sonner";

const LocationContext = createContext(null);

const LS_KEY = "dyntra_location_v1";
const CACHE_KEY = "dyntra_location_cache_v1";
const CACHE_TTL_MS = 30 * 60 * 1000;

function readStored() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function writeStored(loc) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(loc));
  } catch { /* ignore */ }
}

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

function writeCache(cache) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch { /* ignore */ }
}

// Pincode ranges → city map for major Indian metros. Used as a fast local
// lookup before hitting the public India Post API, and as a fallback when
// the API has no record for a valid but less-common pincode (e.g. Greater
// Noida 201316).
const PINCODE_RANGES = [
  { start: 110000, end: 110099, city: "New Delhi", state: "Delhi" },
  { start: 110100, end: 110999, city: "New Delhi", state: "Delhi" },
  { start: 121001, end: 121109, city: "Faridabad", state: "Haryana" },
  { start: 122001, end: 122413, city: "Gurugram", state: "Haryana" },
  { start: 201001, end: 201206, city: "Ghaziabad", state: "Uttar Pradesh" },
  { start: 201207, end: 201319, city: "Noida", state: "Uttar Pradesh" }, // covers 201301..201318 (Noida + Greater Noida)
  { start: 226001, end: 226030, city: "Lucknow", state: "Uttar Pradesh" },
  { start: 400001, end: 400104, city: "Mumbai", state: "Maharashtra" },
  { start: 400601, end: 400615, city: "Thane", state: "Maharashtra" },
  { start: 411001, end: 411062, city: "Pune", state: "Maharashtra" },
  { start: 500001, end: 500099, city: "Hyderabad", state: "Telangana" },
  { start: 560001, end: 560110, city: "Bengaluru", state: "Karnataka" },
  { start: 600001, end: 600130, city: "Chennai", state: "Tamil Nadu" },
  { start: 700001, end: 700157, city: "Kolkata", state: "West Bengal" },
  { start: 380001, end: 382481, city: "Ahmedabad", state: "Gujarat" },
  { start: 302001, end: 302039, city: "Jaipur", state: "Rajasthan" },
  { start: 641001, end: 641050, city: "Coimbatore", state: "Tamil Nadu" },
  { start: 682001, end: 682042, city: "Kochi", state: "Kerala" },
  { start: 160001, end: 160104, city: "Chandigarh", state: "Chandigarh" },
];

// City names we prefer to see over the API's `District` field.
const PREFERRED_CITY_NAMES = new Set([
  "noida", "greater noida", "gurugram", "gurgaon",
  "faridabad", "ghaziabad", "thane", "navi mumbai",
]);

function localPincodeLookup(code) {
  const n = Number(code);
  if (!Number.isFinite(n)) return null;
  for (const range of PINCODE_RANGES) {
    if (n >= range.start && n <= range.end) {
      return { pincode: code, city: range.city, state: range.state, serviceable: true, source: "local" };
    }
  }
  return null;
}

function pickBestCityName(po) {
  const block = (po?.Block || "").trim();
  const district = (po?.District || "").trim();
  const name = (po?.Name || "").trim();
  if (block && PREFERRED_CITY_NAMES.has(block.toLowerCase())) return block;
  if (name && PREFERRED_CITY_NAMES.has(name.toLowerCase())) return name;
  return district || block || name;
}

async function lookupPincodeIndiaPost(code) {
  try {
    const res = await fetch(`https://api.postalpincode.in/pincode/${code}`);
    if (!res.ok) return null;
    const data = await res.json();
    const first = Array.isArray(data) ? data[0] : null;
    if (first && first.Status === "Success" && Array.isArray(first.PostOffice) && first.PostOffice.length > 0) {
      const po = first.PostOffice[0];
      return {
        pincode: code,
        city: pickBestCityName(po),
        state: po.State,
        serviceable: true,
        source: "india-post",
      };
    }
    return null;
  } catch {
    return null;
  }
}

async function lookupPincodeZippo(code) {
  try {
    const res = await fetch(`https://api.zippopotam.us/in/${code}`);
    if (!res.ok) return null;
    const data = await res.json();
    const place = Array.isArray(data?.places) ? data.places[0] : null;
    if (!place) return null;
    return {
      pincode: code,
      city: place["place name"] || place.state,
      state: place.state,
      serviceable: true,
      source: "zippo",
    };
  } catch {
    return null;
  }
}

async function lookupPincodePublic(code) {
  // 1) fast local range lookup (offline)
  const local = localPincodeLookup(code);
  if (local) return local;
  // 2) India Post first (most detailed for Indian pincodes)
  const primary = await lookupPincodeIndiaPost(code);
  if (primary) return primary;
  // 3) Zippopotam.us secondary
  const secondary = await lookupPincodeZippo(code);
  if (secondary) return secondary;
  return { pincode: code, serviceable: false };
}

export function LocationProvider({ children }) {
  const [location, setLocation] = useState({ pincode: "110001", city: "New Delhi", state: "Delhi", serviceable: true });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const stored = readStored();
    if (stored?.pincode) setLocation(stored);
  }, []);

  // Allow LocationDialog to push a coord-only inferred location when the
  // reverse-geocoded pincode is missing (dispatched as `dyntra-location-set`).
  useEffect(() => {
    const handler = (e) => {
      const loc = e?.detail;
      if (!loc || !loc.city) return;
      setLocation(loc);
      writeStored(loc);
    };
    window.addEventListener("dyntra-location-set", handler);
    return () => window.removeEventListener("dyntra-location-set", handler);
  }, []);

  const applyLocation = useCallback((loc) => {
    setLocation(loc);
    writeStored(loc);
  }, []);

  const lookup = useCallback(async (code) => {
    const trimmed = String(code || "").replace(/\D/g, "");
    if (trimmed.length !== 6) {
      toast.error("Please enter a valid 6-digit pincode");
      return { ok: false };
    }
    const cache = readCache();
    const cached = cache[trimmed];
    if (cached && cached.ts && Date.now() - cached.ts < CACHE_TTL_MS && cached.data) {
      applyLocation(cached.data);
      return { ok: true, data: cached.data };
    }
    let data = await lookupPincodePublic(trimmed);
    if (!data) {
      // Offline / API blocked — assume serviceable so the user isn't blocked.
      data = { pincode: trimmed, city: "Your City", state: "", serviceable: true };
    }
    cache[trimmed] = { ts: Date.now(), data };
    writeCache(cache);
    applyLocation(data);
    return { ok: true, data };
  }, [applyLocation]);

  const openLocation = useCallback(() => setOpen(true), []);
  const closeLocation = useCallback(() => setOpen(false), []);

  return (
    <LocationContext.Provider value={{ location, lookup, open, openLocation, closeLocation }}>
      {children}
    </LocationContext.Provider>
  );
}

export function useLocation() {
  const ctx = useContext(LocationContext);
  if (!ctx) {
    return {
      location: { pincode: "110001", city: "New Delhi", state: "Delhi", serviceable: true },
      lookup: async () => ({ ok: false }),
      open: false,
      openLocation: () => {},
      closeLocation: () => {},
    };
  }
  return ctx;
}
